// ============================================================
// PERRY — THE OUTFITTER (Whyllie's counter)
//
// WHY THIS FILE EXISTS, 2026-07-27 (caP'n): "let's add the full
// on customization from the village to the main menu so we can
// kit up and spend our hard earned currency and try to make the
// run easier."
//
// The wares and the pile used to live INLINE inside VillageScene,
// which meant the only way to spend a Crest was to survive the
// road first. That was already a little cruel; the moment every
// parry started paying ⋔ it became actively backwards — the
// player who most needs to buy something is the one who keeps
// dying before the village, and they were the one player who
// could not reach the counter.
//
// So the counter moved out into its own file and BOTH screens
// render this one copy. One source of truth, per the IE
// architecture default: two parallel shop UIs would drift, and
// the one that drifted would be the one nobody was looking at.
//
// It knows nothing about where it is being rendered. It takes a
// Soul, a Crest balance and an onBuy, and it draws a counter.
// ============================================================

import { useState, useEffect } from "react";
import { GM } from "./theme";
import { MetalRule, ensureKeyframes } from "./PerryUI";
import {
  TOMES, CURIOS, TREASURES, GLYPH,
  ownsTreasure, deriveTreasure, treasureLine, stockFor,
  // ── THE SHEATHE, 2026-07-30 ──
  // It is not a shopkeeper and it does not sell anything. It just
  // has opinions about what you pick up, and caP'n wants them
  // heard at the counter: "when you buy weapons your Sheathe will
  // remark on it, sometimes the sword speaks."
  sheatheSays,
} from "./perryData";

const KF = `
@keyframes outIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
`;

// Who is talking, and what colour that is. A map, not a ternary
// chain — the Sheathe is gold and grand, the blade is cold and
// dry, and the world is neither.
const SHEATHE_TINT = {
  sheathe: "#ffe6a8",
  blade:   "#b8c4cc",
  world:   "#8a8078",
};

// Whyllie is a Hollowmere fixture, and a player can now reach his
// counter before ever seeing Hollowmere. Rather than gate it —
// which would re-break the exact thing this file was made to fix —
// he simply turns up. He is that kind of shopkeeper.
const GREETING = {
  met: "“Back already, tiny customer. Everything in here has hit something. Most of it survived.”",
  unmet: "“You have not made it to Hollowmere yet and I have not made it home yet, so as far as I am concerned we are both exactly where we should be. Cart's open. Everything's negotiable except the prices.”",
};

export default function Outfitter({ soul, crests = 0, kindling = 0, onBuy, showHeader = true }) {
  ensureKeyframes("perry-outfitter-keyframes", KF);
  const [note, setNote] = useState("");
  // The Sheathe's beat-list for the last thing that happened. A
  // LIST, because these are exchanges — the scabbard orates, the
  // blade undercuts, the world makes a small sound at the end.
  const [beats, setBeats] = useState(null);

  useEffect(() => {
    if (!note) return undefined;
    const id = setTimeout(() => { setNote(""); setBeats(null); }, 3600);
    return () => clearTimeout(id);
  }, [note]);

  const buy = async (id, kind) => {
    const item = kind === "tome" ? TOMES[id] : kind === "treasure" ? TREASURES[id] : CURIOS[id];
    const res = await onBuy(id, kind, item);
    setNote(res?.message || "");
    // ── THE SHEATHE REMARKS ────────────────────────────────────
    // Only on a weapon, and only on a SUCCESSFUL buy. It is not a
    // receipt and it must never read like one — a failed purchase
    // gets Whyllie's refusal and nothing else, because a scabbard
    // orating about potential over a declined transaction is the
    // joke landing on the wrong beat.
    //
    // Deliberately NOT seeded. The Determinism Law governs the
    // ROAD; the counter is not the road, nothing replays it, and a
    // shopkeeper's ghost saying the identical thing every time is
    // how a character becomes a recording.
    if (kind === "treasure" && res?.ok !== false && !res?.error) {
      setBeats(sheatheSays("bought"));
    } else {
      setBeats(null);
    }
  };

  const owned = (id, kind) =>
    kind === "tome" ? (soul?.tomes?.[id] || 0) > 0
      : kind === "treasure" ? ownsTreasure(soul, id)
      : !!soul?.curios?.[id];

  const held = deriveTreasure(soul);
  const met = !!soul?.milestones?.hollowmere;

  // ── THE STOCK MOVED, AND THIS FILE HAD NOT NOTICED ────────
  // FIXED 2026-07-28. Whyllie's list used to hang off the village
  // (`VILLAGE.wares`), which was right while there was exactly one
  // village. The Drowned Mile pass added a second one and moved
  // the list onto WHYLLIE, where it always belonged — he is not a
  // Hollowmere fixture, he is a man with a cart who turns up where
  // the money is. `VILLAGE.wares` has been `undefined` ever since,
  // and `undefined.map()` throws, which means THE ENTIRE COUNTER
  // HAS BEEN WHITE-SCREENING on both of its doors.
  //
  // `stockFor` is the replacement and it does one thing the old
  // read could not: it honours each row's `gate`, so the fen wares
  // appear the moment you have been to the fen. Nothing else in
  // this file changes — it still knows nothing about where it is
  // being rendered, and it still draws whatever it is handed.
  const wares = stockFor(soul, "wares");
  const armoury = stockFor(soul, "armoury");

  // ── TWO PURSES, ONE TABLE, 2026-07-28 ─────────────────────
  // Whyllie takes Kindling for everything on the cart and Divine
  // Crests for exactly one thing on the high shelf. Rather than
  // branch on which, the lane is read off the price object itself
  // — a third currency is a row here and nothing else changes.
  const PURSE = {
    kindling: { have: kindling, glyph: GLYPH.kd },
    dc: { have: crests, glyph: GLYPH.dc },
  };
  const laneOf = (item) => Object.keys(PURSE).find((k) => item?.price?.[k] != null);

  // Affordability is shown, never enforced here — the actual guard
  // is the transaction in perryProgression, which aborts if short.
  // This just stops a player clicking hopefully at a price they
  // cannot pay and getting a refusal for their trouble.
  const canAfford = (item) => {
    const lane = laneOf(item);
    if (!lane) return true;                 // Anima-priced, or free
    return PURSE[lane].have >= item.price[lane];
  };

  const priceOf = (item) => {
    const lane = laneOf(item);
    if (!lane) return `❤ ${item.price?.anima || 0} ANIMA`;
    return `${PURSE[lane].glyph} ${item.price[lane]}`;
  };

  const buyBtn = (id, kind, item) => {
    const afford = canAfford(item);
    return (
      <button
        onClick={() => afford && buy(id, kind)}
        disabled={!afford}
        style={{
          padding: "5px 11px", borderRadius: 7,
          cursor: afford ? "pointer" : "not-allowed",
          background: afford
            ? "linear-gradient(175deg, #7a4218, #3a2010)"
            : "linear-gradient(175deg, #241610, #150d09)",
          border: `1px solid ${afford ? GM.inkGoldDim : "#2a1e16"}`,
          color: afford ? "#ffe6c2" : GM.vellumFaint,
          fontSize: 11, letterSpacing: 1.5, whiteSpace: "nowrap",
          opacity: afford ? 1 : 0.6,
        }}
      >{priceOf(item)}</button>
    );
  };

  const row = (have) => ({
    padding: "10px 12px", marginBottom: 8, borderRadius: 9,
    background: have ? "rgba(43,184,168,0.06)" : "rgba(0,0,0,0.3)",
    border: `1px solid ${have ? GM.amethystDim : GM.pageBorder}`,
  });

  return (
    <div style={{ animation: "outIn 0.5s ease-out" }}>
      {showHeader && (
        <div style={{ marginBottom: 14 }}>
          <div style={{
            fontSize: 11, letterSpacing: 3, color: GM.inkGoldBright, marginBottom: 6,
          }}>WHYLLIE</div>
          <div style={{
            fontFamily: GM.fontBody, fontSize: 15, lineHeight: 1.6, color: GM.vellum,
          }}>{met ? GREETING.met : GREETING.unmet}</div>
          <MetalRule width={200} style={{ margin: "10px 0 0" }} />
        </div>
      )}

      <SectionTitle>WHYLLIE'S WARES</SectionTitle>
      {wares.map((id) => {
        const kind = TOMES[id] ? "tome" : "curio";
        const item = kind === "tome" ? TOMES[id] : CURIOS[id];
        if (!item) return null;
        const have = owned(id, kind);
        return (
          <div key={id} style={row(have)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div style={{ fontSize: 13, color: GM.vellum, letterSpacing: 1 }}>
                {item.icon} {item.name}
                {item.tag && (
                  <span style={{
                    marginLeft: 7, fontSize: 9, letterSpacing: 1.5,
                    padding: "1px 5px", borderRadius: 4,
                    border: `1px solid ${GM.amethystDim}`, color: GM.amethystBright,
                  }}>{item.tag}</span>
                )}
              </div>
              {have ? (
                <span style={{ fontSize: 10, letterSpacing: 2, color: GM.amethystBright }}>OWNED</span>
              ) : buyBtn(id, kind, item)}
            </div>
            <div style={{ fontSize: 11, lineHeight: 1.5, color: GM.vellumFaint, marginTop: 5 }}>
              {item.desc}
            </div>
          </div>
        );
      })}

      {/* ── WHYLLIE'S ARMOURY ──
          He does not call it that. He calls it "the pile."  */}
      <div style={{ marginTop: 18 }}>
        <SectionTitle>THE PILE</SectionTitle>
        <div style={{ fontSize: 11, color: GM.vellumFaint, lineHeight: 1.5, marginBottom: 9 }}>
          Currently in your paws:
          {" "}<span style={{ color: GM.inkGoldBright }}>{held.icon} {held.name}</span>.
        </div>
        {armoury.map((id) => {
          const item = TREASURES[id];
          if (!item) return null;
          const have = owned(id, "treasure");
          return (
            <div key={id} style={row(have)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 13, color: GM.vellum, letterSpacing: 1 }}>
                  {item.icon} {item.name}
                  <span style={{
                    marginLeft: 7, fontSize: 9, letterSpacing: 1.5,
                    padding: "1px 5px", borderRadius: 4,
                    border: `1px solid ${GM.inkGoldDim}`, color: GM.inkGoldBright,
                  }}>{item.rung?.toUpperCase()}</span>
                </div>
                {have ? (
                  <span style={{ fontSize: 10, letterSpacing: 2, color: GM.amethystBright }}>OWNED</span>
                ) : buyBtn(id, "treasure", item)}
              </div>
              <div style={{ fontSize: 11, lineHeight: 1.5, color: GM.vellumFaint, marginTop: 5 }}>
                {item.desc}
              </div>
              <div style={{ fontSize: 10, color: GM.inkGoldBright, letterSpacing: 1, marginTop: 5 }}>
                {treasureLine(item)}
              </div>
            </div>
          );
        })}
        <div style={{ fontSize: 10, color: GM.vellumFaint, letterSpacing: 1, marginTop: 4 }}>
          BUYING A TREASURE EQUIPS IT. SWAP BACK ANY TIME AT ⚔ ARMOURY.
        </div>
      </div>

      {note && (
        <div style={{
          marginTop: 10, fontSize: 12, lineHeight: 1.5, color: GM.amethystBright,
          animation: "outIn 0.3s ease-out",
        }}>{note}</div>
      )}
      {/* ── THE SHEATHE AND THE BLADE ──────────────────────────
          Three voices, three treatments, one map — `who` is a
          STRING the renderer switches on, so a fourth speaker is
          a row in SHEATHE_LINES and nothing here changes.
          Stage direction (`world`) is centred and dimmed; it is
          the only one that is not somebody talking. */}
      {beats && beats.length > 0 && (
        <div style={{
          marginTop: 12, paddingTop: 10, borderTop: `1px solid ${GM.inkGold}33`,
          animation: "outIn 0.42s ease-out",
        }}>
          {beats.map((b, i) => (
            <div key={i} style={{
              fontSize: 12, lineHeight: 1.55, marginBottom: 4,
              color: SHEATHE_TINT[b.who] || GM.vellumFaint,
              fontStyle: b.who === "world" ? "italic" : "normal",
              textAlign: b.who === "world" ? "center" : "left",
              opacity: b.who === "world" ? 0.7 : 1,
            }}>
              {b.who === "world" ? b.text : `“${b.text}”`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 11, letterSpacing: 4, color: GM.inkGoldBright, marginBottom: 10,
    }}>{children}</div>
  );
}
